import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray
} from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { TableColType } from '@shared/enums/table.enum';
import { IAPIData } from '@shared/interfaces/api.interface';
import { IRecruiter } from '@shared/interfaces/recruiter.interface';
import { ITab } from '@shared/interfaces/tab.interface';
import { ITableCol } from '@shared/interfaces/table.col.interface';
import { LeadTypeNotification as ILeadTypeNotification } from '@shared/interfaces/setting.interface';
import { LeadTypeNotificationsService } from '@shared/services/lead-type-notifications.service';
import { LoggerService } from '@shared/services/logger.service';
import { RecruitersService } from '@shared/services/recruiters.service';
import { ToastService } from '@shared/services/toast.service';
import { TabsetComponent } from 'ngx-bootstrap/tabs';

@Component({
    selector: 'app-modals-lead-type-notifications',
    templateUrl: './modals.lead-type-notifications.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalsLeadTypeNotificationsComponent
  extends ModalDirective<ILeadTypeNotification>
  implements OnInit
{
  @ViewChild(TabsetComponent, { static: true }) public tabset: TabsetComponent;

  public submitKey = String('BUTTONS.SAVE');
  public isLoading = Boolean(true);
  public isButtonsDisabled = Boolean(false);
  public isEmailNotificationDisabled = Boolean(true);
  public entry: FormGroup;
  public rows: IRecruiter[] = [];
  public activeTab = Number(0);
  public leadTypeId: string;
  public leadTypeName = String('');
  // app-modals translates these keys itself
  public title = String('MODALS.LEAD_TYPE_NOTIFICATIONS.TITLE_DEFAULT');
  public modalTitle = String('MODALS.LEAD_TYPE_NOTIFICATIONS.TITLE_DEFAULT');
  public modalDescription = String(
    'MODALS.LEAD_TYPE_NOTIFICATIONS.DESCRIPTION_DEFAULT'
  );

  public tabs: ITab[] = [
    {
      label: 'TABS.NOTIFICATIONS'
    },
    {
      label: 'TABS.ADMINISTRATORS'
    }
  ];

  public cols: Partial<ITableCol>[] = [
    {
      headerText: 'TABLES.HEADERS.NAME',
      type: TableColType.Text,
      selector: 'user.full_name',
      selectorFunction: (data: string) => data || '-'
    },
    {
      headerText: 'TABLES.HEADERS.EMAIL',
      type: TableColType.Text,
      selector: 'user.email'
    },
    {
      headerText: 'TABLES.HEADERS.NOTIFICATIONS',
      headerClassName: 'text-end',
      cellClassName: 'text-end',
      type: TableColType.Toggle,
      selector: 'notifications'
    }
  ];

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _leadTypeNotifications: LeadTypeNotificationsService,
    private readonly _recruiters: RecruitersService,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService,
    private readonly _translate: TranslateService
  ) {
    super();

    this.onButtonClick = this.onButtonClick.bind(this);
  }

  get email_notifications() {
    return this.entry.get('email_notifications') as FormArray;
  }

  addEmailField() {
    this.email_notifications.push(this.createEmailField());
  }

  removeEmailField(index: number) {
    this.email_notifications.removeAt(index);
  }

  ngOnInit(): void {
    this.createForm();

    this.openModal.subscribe(
      (res: { leadTypeId: string; leadTypeName: string }) => {
        this.leadTypeId = res.leadTypeId;
        this.leadTypeName = res.leadTypeName;
        const params = { leadTypeName: res.leadTypeName };
        this.title = this._translate.instant(
          'MODALS.LEAD_TYPE_NOTIFICATIONS.TITLE',
          params
        );
        this.modalTitle = this.title;
        this.modalDescription = this._translate.instant(
          'MODALS.LEAD_TYPE_NOTIFICATIONS.DESCRIPTION',
          params
        );
        this.tabs[0].isActive = true;

        this.getRecruiters();
        this.getNotificationSettings();
      }
    );
  }

  public onDismiss() {
    this.errors = [];

    this.activeTab = 0;
    this.tabs = [
      { label: 'TABS.NOTIFICATIONS', isActive: true },
      { label: 'TABS.ADMINISTRATORS' }
    ];
  }

  public selectTab(index: number): void {
    this.tabs[index].isActive = true;
    this.activeTab = index;
  }

  public onCheckChanged(): void {
    const isSelectedEmailsChecked = Boolean(
      this.entry.get('send_to_custom').value
    );

    const leadEmailControl: AbstractControl = this.entry.get(
      'email_notifications'
    );

    if (isSelectedEmailsChecked) {
      leadEmailControl.enable();
      leadEmailControl.setErrors({});
      this.isEmailNotificationDisabled = false;
    } else {
      leadEmailControl.disable();
      leadEmailControl.setValidators([]);
      leadEmailControl.setErrors({});
      leadEmailControl.markAsUntouched();
      this.isEmailNotificationDisabled = true;
    }

    leadEmailControl.updateValueAndValidity();
  }

  public onButtonClick(_e: Event, r: IRecruiter): void {
    r.notifications = !r.notifications;

    const url = `PATCH /recruiters/${r.id}`;
    this._recruiters.patch(r.apiData).subscribe(
      (res: IRecruiter) => {
        this._logger.info(this.constructorName, url, res);

        this._toast.success('TOASTS.RECRUITER_UPDATED');
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this._toast.error('TOASTS.RECRUITER_UPDATE_FAILED');
      }
    );
  }

  public onSubmit({
    valid,
    value
  }: {
    valid: boolean;
    value: ILeadTypeNotification;
  }): void {
    if (valid) {
      this.isButtonsDisabled = true;

      const customEmails = this.email_notifications.value
        .map((e: { email: string }) => e.email)
        .filter((e: string) => e)
        .join(',');

      const data: IAPIData = {
        data: {
          type: 'LeadTypeNotification',
          id: value.id,
          attributes: {
            send_to_root: value.send_to_root,
            send_to_assigned: value.send_to_assigned,
            send_to_custom: value.send_to_custom,
            custom_emails: customEmails
          }
        }
      };

      const url = `PATCH /lead_types/${this.leadTypeId}/notification`;
      this._leadTypeNotifications.update(this.leadTypeId, data).subscribe(
        (res: ILeadTypeNotification) => {
          this._logger.info(this.constructorName, url, res);

          this._toast.success('TOASTS.NOTIFICATIONS_UPDATED');
          this.isButtonsDisabled = false;
          this.resetModal();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);

          this.isButtonsDisabled = false;
          this._toast.error('TOASTS.NOTIFICATIONS_UPDATE_FAILED');
        }
      );
    }
  }

  protected createForm(): void {
    this.entry = this._fb.group({
      id: [''],
      send_to_root: [false],
      send_to_assigned: [true],
      send_to_custom: [false],
      email_notifications: this._fb.array([this.createEmailField()])
    });
  }

  private getRecruiters() {
    const url = `GET /recruiters`;

    const filter: any = {
      page_size: 250
    };

    this._recruiters.get(filter).subscribe(
      (res: IRecruiter[]) => {
        this._logger.info(this.constructorName, url, res);
        this.rows = res;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  private getNotificationSettings() {
    const url = `GET /lead_types/${this.leadTypeId}/notification`;

    this._leadTypeNotifications.get(this.leadTypeId).subscribe(
      (res: ILeadTypeNotification) => {
        this._logger.info(this.constructorName, url, res);

        // Populate email fields if custom_emails exist
        if (res.custom_emails) {
          this.populateEmailFieldsFromString(res.custom_emails);
        }

        this.entry.patchValue({
          id: res.id,
          send_to_root: res.send_to_root,
          send_to_assigned: res.send_to_assigned,
          send_to_custom: res.send_to_custom
        });

        this.onCheckChanged();
        this.isLoading = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        // If notification doesn't exist yet, use defaults
        this.entry.patchValue({
          send_to_root: false,
          send_to_assigned: true,
          send_to_custom: false
        });

        this.onCheckChanged();
        this.isLoading = false;
      }
    );
  }

  private populateEmailFieldsFromString(emailString: string) {
    if (!emailString) return;

    const emails = emailString
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);
    const emailFormGroups = emails.map((email) => this.createEmailField(email));
    const emailFormArray = this._fb.array(emailFormGroups);
    this.entry.setControl('email_notifications', emailFormArray);
  }

  private createEmailField(email: string = ''): FormGroup {
    return this._fb.group({
      email: [email, [Validators.required, Validators.email]]
    });
  }
}
